<script setup lang="ts">
import { formatBusinessDate } from '~/shared-kernel/domain/business-time'
import type { PublicPreregistration } from '~/shared-kernel/domain/preregistration'
import { registrationIsOpen } from '~/shared-kernel/domain/preregistration'
const props = defineProps<{ value: PublicPreregistration }>()
const emit = defineEmits<{ refresh: [] }>()
const { t, locale } = useI18n()
const localePath = useLocalePath()
const now = shallowRef(Date.now())
let timer: ReturnType<typeof setInterval> | undefined
onMounted(() => {
  timer = setInterval(() => {
    now.value = Date.now()
  }, 1000)
})
onBeforeUnmount(() => {
  if (timer) clearInterval(timer)
})
const open = computed(() => registrationIsOpen(props.value, now.value))
const date = (value: string | null) =>
  value
    ? formatBusinessDate(value, locale.value, {
        dateStyle: 'long',
        timeStyle: 'short'
      })
    : t('preregistration.datePending')
const login = computed(() => ({
  path: localePath('/account/login'),
  query: { redirect: localePath(`/events/${props.value.event.slug}`) }
}))
</script>
<template>
  <section class="preregistration-access" aria-labelledby="priority-title">
    <UIcon
      name="i-lucide-ticket"
      class="preregistration-access__icon"
      aria-hidden="true"
    />
    <TqBadge>{{ t('preregistration.priorityBadge') }}</TqBadge>
    <h1 id="priority-title">{{ value.event.name }}</h1>
    <h2>
      {{
        t(
          value.enrollment
            ? 'preregistration.youAreRegistered'
            : 'preregistration.accessTitle'
        )
      }}
    </h2>
    <p>
      <template
        v-if="
          value.event.status === 'preregistration' && value.campaign && !open
        "
      >
        {{
          now < Date.parse(value.campaign.opensAt)
            ? t('preregistration.registrationComing', {
                date: date(value.campaign.opensAt)
              })
            : t('preregistration.registrationEnded')
        }}
      </template>
      <template v-else>
        {{
          t(
            value.access.state === 'priority'
              ? 'preregistration.priorityMessage'
              : 'preregistration.beforeSaleMessage'
          )
        }}
      </template>
    </p>
    <dl>
      <div>
        <dt>{{ t('preregistration.priorityStarts') }}</dt>
        <dd>{{ date(value.access.saleStartsAt) }}</dd>
      </div>
      <div>
        <dt>{{ t('preregistration.publicStarts') }}</dt>
        <dd>{{ date(value.access.publicStartsAt) }}</dd>
      </div>
    </dl>
    <p>{{ t('preregistration.noReservation') }}</p>
    <div class="preregistration-access__actions">
      <TqButton
        v-if="open && !value.enrollment"
        :to="localePath(`/events/${value.event.slug}/preregister`)"
        icon="i-lucide-sparkles"
        >{{ t('preregistration.join') }}</TqButton
      >
      <TqButton v-if="!value.customer" variant="secondary" :to="login">{{
        t('preregistration.signInPriority')
      }}</TqButton>
      <TqButton variant="ghost" @click="emit('refresh')">{{
        t('actions.refresh')
      }}</TqButton>
    </div>
  </section>
</template>
<style scoped>
.preregistration-access {
  display: grid;
  justify-items: center;
  gap: 1rem;
  max-width: 44rem;
  margin: 2rem auto;
  padding: clamp(1.5rem, 4vw, 3rem);
  border: 1px solid var(--tq-border);
  border-radius: var(--tq-radius-lg);
  background: var(--tq-bg-elevated);
  text-align: center;
}
.preregistration-access__icon {
  width: 3rem;
  height: 3rem;
  color: var(--tq-teal-readable);
}
.preregistration-access h1,
.preregistration-access h2,
.preregistration-access p {
  margin: 0;
}
.preregistration-access p {
  color: var(--tq-text-muted);
}
.preregistration-access dl {
  display: grid;
  gap: 1rem;
}
.preregistration-access dt {
  color: var(--tq-text-muted);
  font-size: 0.85rem;
}
.preregistration-access dd {
  margin: 0.25rem 0 0;
  font-weight: var(--tq-font-weight-regular);
}
.preregistration-access__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
}
</style>
